import { useCallback, useEffect, useState } from "react";
import { HolidayRogueLab } from "./HolidayRogueLab";
import { tx, txr } from "../lib/tx";

type Props = {
  onDone: () => void;
  onStageChange?: (stage: string) => void;
  doneLabel?: string;
};

type Phase = "hunt_open" | "hunt_map" | "hunt_teach" | "hunt_lab";
type NetId = "hotel" | "twin" | "bar" | "guest" | "free";

type Net = {
  id: NetId;
  ssid: string;
  place: string;
  rssi: number;
  ch: number;
  bssid: string;
};

const NETWORKS: Net[] = [
  { id: "hotel", ssid: "GrandHotel_WiFi", place: "Hotel roof", rssi: 4, ch: 6, bssid: "A4:91:B1:2C:88:10" },
  { id: "twin", ssid: "GrandHotel_WiFi", place: "Laptop by the pool", rssi: 5, ch: 6, bssid: "02:1A:7C:9E:44:B3" },
  { id: "bar", ssid: "Pool_Bar", place: "Pool bar", rssi: 3, ch: 11, bssid: "C8:3A:35:01:9F:22" },
  { id: "guest", ssid: "Guest-WiFi", place: "Hotel building", rssi: 2, ch: 1, bssid: "A4:91:B1:2C:88:21" },
  { id: "free", ssid: "FREE_HOTEL_WIFI", place: "Phone hotspot", rssi: 4, ch: 6, bssid: "DA:A1:19:60:0E:77" },
];

const ORIGIN: Record<NetId, { x: number; y: number; lx: number; ly: number }> = {
  hotel: { x: 248, y: 86, lx: 248, ly: 52 },
  guest: { x: 292, y: 102, lx: 378, ly: 88 },
  bar: { x: 92, y: 208, lx: 92, ly: 246 },
  twin: { x: 348, y: 236, lx: 418, ly: 214 },
  free: { x: 156, y: 268, lx: 156, ly: 304 },
};

function Bars({ n }: { n: number }) {
  return (
    <span className="hrh-bars" aria-hidden>
      {[1, 2, 3, 4, 5].map((i) => (
        <i key={i} className={i <= n ? "on" : ""} />
      ))}
    </span>
  );
}

function RadarIcon() {
  return (
    <svg viewBox="0 0 40 40" aria-hidden>
      <circle cx="20" cy="20" r="16" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="20" cy="20" r="9" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M20 20 L31 11" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="20" cy="20" r="2.2" fill="currentColor" />
    </svg>
  );
}

function originTone(id: NetId, found: boolean) {
  if (id === "twin" && found) return "rogue";
  if (found) return "idle";
  return "hunt";
}

function SatelliteMap({
  found,
  onPick,
}: {
  found: boolean;
  onPick: (id: NetId) => void;
}) {
  return (
    <svg className="hrh-sat" viewBox="0 0 480 360" role="img" aria-label={tx("Satellite view of the hotel pool, with each Wi-Fi network labeled at its source")}>
      <defs>
        <linearGradient id="hrhOcean" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1b6a86" />
          <stop offset="100%" stopColor="#0e3d52" />
        </linearGradient>
        <linearGradient id="hrhLawn" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3d7a3a" />
          <stop offset="55%" stopColor="#2f6a34" />
          <stop offset="100%" stopColor="#4a7c3c" />
        </linearGradient>
        <radialGradient id="hrhPool" cx="50%" cy="42%" r="62%">
          <stop offset="0%" stopColor="#7ee3ef" />
          <stop offset="55%" stopColor="#2aa8c4" />
          <stop offset="100%" stopColor="#157a96" />
        </radialGradient>
        <filter id="hrhShade" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="1.2" dy="1.8" stdDeviation="1.4" floodOpacity=".35" />
        </filter>
      </defs>

      <rect width="480" height="360" fill="url(#hrhOcean)" />
      <path d="M18 8h444c8 0 14 8 12 16L452 348c-1 8-8 14-16 14H28c-8 0-14-7-15-15L6 24C4 14 10 8 18 8z" fill="url(#hrhLawn)" />
      <path d="M22 300c40-18 90-10 140 4 48 14 110 8 160-8 36-12 78-8 118 10v54H22z" fill="#c4a56a" opacity=".9" />
      <path d="M30 292c36-8 70 2 108 10 52 12 100 4 148-6" fill="none" stroke="#d8c08a" strokeWidth="7" />

      <g filter="url(#hrhShade)">
        <path d="M118 28h210l18 18v78H100V46z" fill="#e8e2d4" />
        <path d="M118 28h210l18 18H136z" fill="#f4efe3" />
        <rect x="132" y="52" width="28" height="22" rx="2" fill="#6aa8c2" />
        <rect x="168" y="52" width="28" height="22" rx="2" fill="#6aa8c2" />
        <rect x="204" y="52" width="28" height="22" rx="2" fill="#5b9cb8" />
        <rect x="240" y="52" width="28" height="22" rx="2" fill="#6aa8c2" />
        <rect x="276" y="52" width="28" height="22" rx="2" fill="#6aa8c2" />
        <rect x="148" y="82" width="22" height="16" rx="1" fill="#8a93a0" />
        <rect x="178" y="82" width="22" height="16" rx="1" fill="#8a93a0" />
        <rect x="208" y="82" width="22" height="16" rx="1" fill="#7d8794" />
        <rect x="258" y="78" width="36" height="24" rx="2" fill="#c9c2b4" />
        <text x="198" y="44" textAnchor="middle" className="hrh-label-roof">{tx("GRAND HOTEL")}</text>
      </g>
      <rect x="98" y="118" width="248" height="10" fill="#d9d0c0" />
      <rect x="86" y="126" width="42" height="28" rx="3" fill="#d7c8a8" />

      <g filter="url(#hrhShade)">
        <rect x="48" y="168" width="72" height="48" rx="4" fill="#efe6d2" />
        <rect x="54" y="174" width="28" height="16" rx="1" fill="#6aa8c2" />
        <text x="84" y="210" textAnchor="middle" className="hrh-label-roof">{tx("BAR")}</text>
      </g>

      <ellipse cx="236" cy="214" rx="108" ry="64" fill="#d9c9a4" />
      <ellipse cx="236" cy="214" rx="92" ry="50" fill="url(#hrhPool)" />
      <ellipse cx="220" cy="198" rx="28" ry="10" fill="#fff" opacity=".18" />
      <path d="M160 214h152M236 172v84" stroke="#8fd3de" strokeWidth=".7" opacity=".45" />

      {[
        [70, 140], [400, 128], [418, 188], [52, 250], [430, 270], [100, 300],
      ].map(([x, y], i) => (
        <g key={i} transform={`translate(${x} ${y})`}>
          <circle r="11" fill="#1f5c28" />
          <circle r="6" fill="#2f7a38" />
          <rect x="-1.2" y="6" width="2.4" height="10" fill="#5a3a22" />
        </g>
      ))}

      <g className="hrh-loungers">
        <rect x="318" y="198" width="34" height="12" rx="2" fill="#c97848" transform="rotate(-18 335 204)" />
        <rect x="332" y="218" width="34" height="12" rx="2" fill="#c97848" transform="rotate(-12 349 224)" />
        <rect x="140" y="248" width="30" height="11" rx="2" fill="#c97848" transform="rotate(16 155 253)" />
        <rect x="168" y="262" width="30" height="11" rx="2" fill="#b86a3e" transform="rotate(10 183 267)" />
      </g>

      <g className="hrh-people">
        <ellipse cx="210" cy="204" rx="4.5" ry="7" fill="#c58a62" />
        <ellipse cx="248" cy="222" rx="5" ry="7.5" fill="#e8c4a0" />
        <ellipse cx="198" cy="230" rx="4" ry="6.5" fill="#8d5a3c" />
        <circle cx="154" cy="256" r="4.2" fill="#f0c8a4" />
        <circle cx="176" cy="270" r="3.8" fill="#c58a62" />
        <circle cx="78" cy="188" r="3.6" fill="#d7b08a" />
        <g transform="translate(348 228)">
          <ellipse rx="6.2" ry="8.4" fill="#c4a07a" />
          <rect x="5" y="0" width="14" height="10" rx="1.4" fill="#1c2430" />
          <rect x="6" y="-2" width="12" height="4" rx=".7" fill="#3a4558" />
          <rect x="8" y="2.2" width="8" height="4.6" fill="#5ad0e8" opacity=".9" />
        </g>
      </g>

      {(Object.keys(ORIGIN) as NetId[]).map((id) => {
        const p = ORIGIN[id];
        const net = NETWORKS.find((n) => n.id === id)!;
        const t = originTone(id, found);
        const w = Math.max(124, net.ssid.length * 7.6);
        return (
          <g key={id} className={`hrh-origin hrh-origin--${t}`}>
            <line x1={p.x} y1={p.y} x2={p.lx} y2={p.ly + 10} className="hrh-pin-lead" />
            <circle className="hrh-ring hrh-ring-a" cx={p.x} cy={p.y} r="22" />
            <circle className="hrh-ring hrh-ring-b" cx={p.x} cy={p.y} r="14" />
            <circle className="hrh-hit" cx={p.x} cy={p.y} r="18" onClick={() => onPick(id)} />
            <circle className="hrh-dot" cx={p.x} cy={p.y} r="6.5" pointerEvents="none" />
            <g pointerEvents="none">
              <rect className="hrh-pin-bg" x={p.lx - w / 2} y={p.ly - 20} width={w} height="34" rx="5" />
              <text x={p.lx} y={p.ly - 6} textAnchor="middle" className="hrh-pin-lab">{net.ssid}</text>
              <text x={p.lx} y={p.ly + 8} textAnchor="middle" className="hrh-pin-place">{net.place}</text>
            </g>
          </g>
        );
      })}
    </svg>
  );
}

export function HolidayRogueHunt({ onDone, onStageChange, doneLabel }: Props) {
  const [phase, setPhase] = useState<Phase>("hunt_open");
  const [found, setFound] = useState(false);
  const [miss, setMiss] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    if (phase === "hunt_lab") return;
    onStageChange?.(phase);
  }, [phase, onStageChange]);

  useEffect(() => {
    if (phase !== "hunt_map") return;
    const t = window.setTimeout(() => setZoomed(true), 220);
    return () => window.clearTimeout(t);
  }, [phase]);

  const pick = useCallback((id: NetId) => {
    if (phase !== "hunt_map") return;
    if (id === "twin") {
      setFound(true);
      setMiss(null);
      setPhase("hunt_teach");
      return;
    }
    if (id === "hotel") {
      setMiss("That GrandHotel_WiFi is the hotel's real access point: it comes from the roof, its vendor-style BSSID matches the other hotel radio, and the map places it on hotel infrastructure. Try another pin.");
      return;
    }
    if (id === "guest") {
      setMiss("Guest-WiFi is another hotel radio on the building — open or not, it is not copying GrandHotel_WiFi. The rogue is the twin of a trusted name. Try again.");
      return;
    }
    if (id === "bar") {
      setMiss("Pool_Bar is a different SSID at the pool bar. A suspicious or open network is not automatically the evil twin we are hunting. Try again.");
      return;
    }
    setMiss("FREE_HOTEL_WIFI is a phone hotspot with a giveaway name. The attack copies the trusted hotel SSID so it looks legitimate. Try again.");
  }, [phase]);

  useEffect(() => {
    const handleGuideAction = (event: Event) => {
      const requested = (event as CustomEvent<{ stage?: string }>).detail?.stage;
      if (requested && !requested.startsWith("hunt_")) return;
      if (phase === "hunt_open") setPhase("hunt_map");
      else if (phase === "hunt_teach") onDone();
    };
    window.addEventListener("cf-holiday-guide-action", handleGuideAction);
    return () => window.removeEventListener("cf-holiday-guide-action", handleGuideAction);
  }, [phase, onDone]);

  return (
    <>
      {phase === "hunt_lab" && (
        <HolidayRogueLab
          onStageChange={onStageChange}
          onDone={() => setPhase("hunt_teach")}
        />
      )}
      {phase === "hunt_open" && (
        <div className="ht-home hrh-home">
          <div className="ht-owner">{tx("Elena's company iPad")}</div>
          <div className="ht-app-grid">
            {["Mail", "Browser", "Radar", "Files", "Calendar", "Notes"].map((app) => (
              <button
                key={app}
                className={app === "Radar" ? "cf-pulse-target ht-action-target" : ""}
                onClick={() => app === "Radar" && setPhase("hunt_map")}
              >
                <span className={`ht-app-icon ${app === "Radar" ? "ht-app-radar" : `ht-app-${app.toLowerCase()}`}`}>
                  {app === "Radar" ? <RadarIcon /> : app.slice(0, 2)}
                </span>
                <small>{app === "Radar" ? tx("Wi-Fi Radar") : app}</small>
                {app === "Radar" && <em className="ht-click-chip">{tx("Click here")}</em>}
              </button>
            ))}
          </div>
        </div>
      )}

      {phase !== "hunt_open" && phase !== "hunt_lab" && (
        <div className="hrh-scan">
          <header className="hrh-head">
            <div>
              <strong>{tx("Hunt the Rogue")}</strong>
              <small>{tx("Each network is pinned to its source — find which one is the rogue network")}</small>
            </div>
            <span className="hrh-live">{tx("LIVE")}</span>
          </header>

          <ul className="hrh-nets">
            {NETWORKS.map((n) => (
              <li
                key={n.id}
                className={`${n.ssid === "GrandHotel_WiFi" ? "is-dup" : ""} ${n.id === "twin" && found ? "is-rogue" : ""}`}
              >
                <span className="hrh-ssid">{n.ssid}</span>
                <span className="hrh-place">{n.place}</span>
                <span className="hrh-meta">{txr("ch {ch}", { ch: n.ch })}</span>
                <Bars n={n.rssi} />
              </li>
            ))}
          </ul>

          {phase === "hunt_map" && (
            <div className={`hrh-mapwrap ${zoomed ? "is-zoomed" : ""}`}>
              <div className="hrh-world">
                <SatelliteMap found={found} onPick={pick} />
                <div className="hrh-sweep" aria-hidden />
              </div>
              <span className="hrh-sat-badge">{tx("SAT · pool deck")}</span>
              <div className={`hrh-map-hint${miss ? " is-miss" : ""}`}>
                {miss
                  ?? tx("Anyone can name a phone or laptop hotspot GrandHotel_WiFi. Compare the two identical names, their signal strength and where the scanner locates each source. The official hotel network should not be broadcasting from a guest laptop beside the pool.")}
              </div>
            </div>
          )}

          {phase === "hunt_teach" && (
            <div className="hrh-teach-full">
              <small>{tx("HUNT THE ROGUE")}</small>
              <h3>{tx("Correct — the laptop by the pool is the rogue.")}</h3>
              <p>
                {txr("You found the twin. The SSID is identical to the hotel, but the source is a guest laptop on a lounger, the signal is stronger up close, and the BSSID starts with {b} (locally administered — typical of a software access point, not a hotel radio).", { b: <b>02:</b> })}
              </p>
              <p>
                {tx("Treat those as combined evidence in this exercise, not a universal proof rule. Anyone with a phone can copy a trusted name. Elena already joined an open hotel SSID on this iPad — that is how an evil twin gets a seat next to the real network.")}
              </p>
              <ul>
                <li>{txr("<0>Hotel roof</0> — real GrandHotel_WiFi (managed infrastructure)", {}, [<b />])}</li>
                <li>{txr("<0>Laptop by the pool</0> — rogue GrandHotel_WiFi (evil twin)", {}, [<b />])}</li>
              </ul>
              <div className="hrh-teach-actions">
                <button type="button" className="ht-primary cf-pulse-target ht-action-target" onClick={onDone}>
                  {doneLabel ?? tx("Continue Elena’s story")}<em className="ht-click-chip">{tx("Click here")}</em>
                </button>
                <button type="button" className="hrh-lab-cta" onClick={() => setPhase("hunt_lab")}>
                  {tx("Play: How hackers set up rogue access points")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
